import EntityRequest, { Provenance } from '../store/schema/EntityRequest';
import ExecuteSuggestionsRequest from '../store/schema/ExecuteSuggestionsRequest';
import isUserBoundToLinkedIn from '../utils/isUserBoundToLinkedIn';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { makeGetRequest, makePostRequest } from 'owa-ows-gateway';
import { SubstrateSearchSuggestionsResponse } from 'owa-search-service';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { SUBSTRATE_SUGGESTION_FIELDS } from 'owa-substrate-people-suggestions/lib/utils/substrateSuggestionUtils';
import { getAccessTokenforResource } from 'owa-tokenprovider';
import { trace } from 'owa-trace';

const INIT_GET_ENDPOINT = '/search/api/v1/init?';
const ENTERPRISE_TOKEN_RESOURCE = 'https://outlook.office365.com/search';
const SUGGESTIONS_POST_ENDPOINT = '/search/api/v1/suggestions?';
const SCENARIO_NAME = 'owa.react.compose';
const MAX_ENTRIES_DIRECTORY = '20';
const MAX_ENTRIES_TYPEDOWN = '5';

export default async function substrateSuggestions(
    queryString: string,
    includeDirectory: boolean,
    includeFeatureData?: boolean,
    includeImplicitGroups?: boolean,
    currentRecipients?: string[]
): Promise<SubstrateSearchSuggestionsResponse> {
    if (queryString == '') {
        return makeInitRequest();
    } else {
        let entityRequest = await createEntityRequest(
            queryString,
            includeDirectory,
            includeFeatureData,
            currentRecipients
        );
        let suggestionJsonRequest = createSuggestionsJsonRequest(entityRequest);

        return makePostRequest(
            SUGGESTIONS_POST_ENDPOINT + getParams(),
            suggestionJsonRequest,
            undefined /*correlationId*/,
            false /*returnFullResponse*/,
            await getCustomHeaders()
        )
            .then(resp => {
                return resp;
            })
            .catch(err => {
                trace.error(err);
                return null;
            });
    }
}

async function makeInitRequest() {
    return makeGetRequest(
        INIT_GET_ENDPOINT + getParams(),
        undefined /*correlationId*/,
        true /*returnFullResponse*/,
        await getCustomHeaders()
    )
        .then(resp => {
            return resp.json() as Promise<SubstrateSearchSuggestionsResponse>;
        })
        .catch(err => {
            trace.error(err);
            return null;
        });
}

async function createEntityRequest(
    queryString: string,
    includeDirectory: boolean,
    includeFeatureData?: boolean,
    currentRecipients?: string[]
): Promise<EntityRequest> {
    const isLinkedInEnabled =
        isFeatureEnabled('rp-linkedInPeopleSuggestions') && isUserBoundToLinkedIn();
    let entityRequest: EntityRequest = {
        Query: {
            QueryString: queryString,
        },
        EntityType: 'People',
        Provenances: getProvenances(includeDirectory, isLinkedInEnabled),
        Size: includeDirectory ? MAX_ENTRIES_DIRECTORY : MAX_ENTRIES_TYPEDOWN,
    };

    if (currentRecipients && currentRecipients.length > 0) {
        entityRequest.Context = createEntityRequestContext(currentRecipients);
    }

    entityRequest.Fields = SUBSTRATE_SUGGESTION_FIELDS;

    if (includeFeatureData) {
        entityRequest.Fields.push('FeatureData');
    }

    return entityRequest;
}

function createSuggestionsJsonRequest(entityRequest: EntityRequest): ExecuteSuggestionsRequest {
    return {
        AppName: 'OWA',
        Scenario: {
            Name: SCENARIO_NAME,
        },
        Cvid: getGuid(),
        EntityRequests: [entityRequest],
    };
}

function createEntityRequestContext(currentRecipients: string[]): { [index: string]: string } {
    let context: { [index: string]: string } = {};

    if (currentRecipients) {
        context.RecipientsTo = currentRecipients.toString();
    }

    return context;
}

function getProvenances(includeDirectory: boolean, isLinkedInEnabled: boolean): Provenance[] {
    let provenances: Provenance[] = ['Mailbox'];

    if (includeDirectory) {
        provenances.push('Directory');
    }

    if (isLinkedInEnabled) {
        provenances.push('LinkedIn');
    }

    return provenances;
}

function getParams(): string {
    const params = {
        scenario: SCENARIO_NAME,
    };

    return Object.keys(params)
        .map(key => `${key}=${params[key]}`)
        .join('&');
}

async function getCustomHeaders(): Promise<any> {
    if (isFeatureEnabled('rp-linkedInPeopleSuggestions') && isUserBoundToLinkedIn()) {
        if (isConsumer()) {
            return {
                'X-User-Context': 'litoken=bearer',
            };
        }

        const linkedInToken = await getAccessTokenforResource(ENTERPRISE_TOKEN_RESOURCE);
        return {
            'X-User-Context': `litoken=${linkedInToken}`,
        };
    }
    return null;
}
